import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, ShoppingCart, FileText, Loader2, ShieldCheck } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Cart() {
  const { items, removeItem, total, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  const handleCheckout = async () => {
    if (!user) {
      toast.info("Please log in to continue");
      navigate("/login");
      return;
    }
    setProcessing(true);
    try {
      const { data: order } = await api.post("/checkout/create-order", {
        note_ids: items.map((i) => i.id),
      });

      const finalize = async (payload) => {
        await api.post("/checkout/verify", payload);
        clearCart();
        toast.success("Payment successful! Notes added to your library.");
        navigate("/purchases");
      };

      if (order.mode === "razorpay") {
        const ok = await loadRazorpay();
        if (!ok) {
          toast.error("Could not load payment gateway");
          setProcessing(false);
          return;
        }
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          name: "NotesBazaar",
          description: `${order.note_ids.length} note(s)`,
          order_id: order.order_id,
          prefill: { name: user.name, email: user.email },
          theme: { color: "#2563EB" },
          handler: async (res) => {
            await finalize({
              order_id: order.order_id,
              note_ids: order.note_ids,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_signature: res.razorpay_signature,
            });
          },
          modal: { ondismiss: () => setProcessing(false) },
        });
        rzp.open();
      } else {
        // MOCKED payment flow (no Razorpay keys configured)
        await new Promise((r) => setTimeout(r, 900));
        await finalize({ order_id: order.order_id, note_ids: order.note_ids });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setProcessing(false);
    }
  };

  if (items.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center" data-testid="cart-empty">
        <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
          <ShoppingCart className="h-8 w-8 text-slate-400" />
        </div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Your cart is empty</h1>
        <p className="text-muted-foreground mt-2">Browse the marketplace and add some notes.</p>
        <Link to="/">
          <Button className="mt-6 rounded-full">Browse notes</Button>
        </Link>
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
      <h1 className="font-heading font-bold text-3xl tracking-tight text-slate-900 mb-8">
        Your cart <span className="text-muted-foreground">({items.length})</span>
      </h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3" data-testid="cart-items">
          {items.map((i) => (
            <div
              key={i.id}
              className="flex items-center gap-4 p-4 rounded-xl border border-border bg-white"
              data-testid={`cart-item-${i.id}`}
            >
              <div className="h-16 w-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                {i.has_cover ? (
                  <img src={`${API}/notes/${i.id}/cover`} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-white/80" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/note/${i.id}`} className="font-medium text-slate-900 hover:text-primary line-clamp-1">
                  {i.title}
                </Link>
                <p className="text-xs text-muted-foreground">
                  {i.subject} · {i.seller_name}
                </p>
              </div>
              <span className="font-heading font-semibold text-slate-900">₹{i.price}</span>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => removeItem(i.id)}
                data-testid={`remove-item-${i.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="p-6 rounded-2xl border border-border bg-slate-50 sticky top-24">
            <h2 className="font-heading font-semibold text-lg text-slate-900">Order summary</h2>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Items</span>
                <span>{items.length}</span>
              </div>
              <div className="flex justify-between font-heading font-bold text-xl text-slate-900 pt-3 border-t border-border">
                <span>Total</span>
                <span data-testid="cart-total">₹{total.toFixed(2)}</span>
              </div>
            </div>
            <Button
              className="w-full mt-6 rounded-full gap-2"
              size="lg"
              onClick={handleCheckout}
              disabled={processing}
              data-testid="checkout-button"
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Processing...
                </>
              ) : (
                <>Pay ₹{total.toFixed(2)}</>
              )}
            </Button>
            <p className="mt-4 text-xs text-muted-foreground flex items-center gap-1.5 justify-center">
              <ShieldCheck className="h-4 w-4 text-primary" /> Secure checkout · Instant access
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
